import type { Recipe, WeekMeal, MealType, Rating } from '../types';
import { RatingButtons } from './RatingButtons';
import { PremadeChip } from './PremadeChip';
import { EmojiBadge } from './EmojiBadge';

export function DayCard(props: {
  date: string;
  dayName: string;
  isToday: boolean;
  meals: WeekMeal[];
  enabledMealTypes: MealType[];
  recipeById: (id: string) => Recipe | undefined;
  onRate: (date: string, mealType: MealType, recipeId: string, rating: Rating) => void;
  onTapSlot: (date: string, mealType: MealType) => void;
  onClearSlot: (date: string, mealType: MealType, recipeId: string) => void;
  onOpenRecipe: (recipeId: string) => void;
}) {
  return (
    <div style={{
      background: props.isToday ? 'white' : 'var(--cream)',
      border: props.isToday ? '2px solid var(--pink-soft)' : '1px solid transparent',
      borderRadius: 14, padding: '12px 14px', marginBottom: 10,
      boxShadow: props.isToday ? 'var(--shadow-card-hover)' : 'var(--shadow-card)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <strong style={{ fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {props.dayName}
          {props.isToday && (
            <span style={{
              marginLeft: 6, background: 'var(--pink-primary)', color: 'var(--on-primary)',
              fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
            }}>Today</span>
          )}
        </strong>
        <span style={{ fontSize: 12, color: 'var(--plum-soft)' }}>{props.date}</span>
      </div>
      {props.enabledMealTypes.map((mt, i) => {
        const slotMeals = props.meals.filter(m => m.meal_type === mt && m.recipe_id);
        const hasMeals = slotMeals.length > 0;
        return (
          <div key={mt} style={{
            padding: '8px 0',
            borderTop: i === 0 ? 'none' : '1px dashed var(--line)',
          }}>
            <div className="label-caps" style={{ marginBottom: 4 }}>{mt}</div>
            {hasMeals ? (
              <>
                {slotMeals.map((meal, idx) => {
                  const recipe = props.recipeById(meal.recipe_id!);
                  if (!recipe) return null;
                  return (
                    <div key={recipe.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0',
                      borderTop: idx === 0 ? 'none' : '1px solid var(--hairline)',
                    }}>
                      <button
                        type="button"
                        aria-label={`Open ${recipe.name}`}
                        onClick={() => props.onOpenRecipe(recipe.id)}
                        style={{
                          flexShrink: 0, width: 56, height: 56,
                          padding: 0, border: 'none', borderRadius: 8,
                          overflow: 'hidden', cursor: 'pointer',
                          background: 'transparent',
                        }}
                      >
                        <div style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden' }}>
                          <EmojiBadge
                            category={recipe.category}
                            cuisine={recipe.cuisine}
                            imageUrl={recipe.image_url}
                            imageFocus={recipe.image_focus}
                            height={56}
                          />
                        </div>
                      </button>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => props.onOpenRecipe(recipe.id)}
                          style={{
                            background: 'none', border: 'none', padding: 0, color: 'var(--ink)',
                            font: 'inherit', cursor: 'pointer', textAlign: 'left',
                            fontWeight: 600, fontSize: 14, lineHeight: 1.25,
                          }}
                        >{recipe.name}</button>
                        <PremadeChip visible={recipe.is_make_ahead} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                          <RatingButtons
                            value={meal.rating}
                            onChange={r => props.onRate(props.date, mt, recipe.id, r)}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${recipe.name} from plan`}
                        onClick={() => props.onClearSlot(props.date, mt, recipe.id)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--muted-soft)', fontSize: 18, lineHeight: 1,
                          padding: '4px 6px', borderRadius: 6,
                        }}
                      >×</button>
                    </div>
                  );
                })}
                <div
                  onClick={() => props.onTapSlot(props.date, mt)}
                  style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer', fontWeight: 500, padding: '4px 0' }}
                >+ Add another</div>
              </>
            ) : (
              <div
                onClick={() => props.onTapSlot(props.date, mt)}
                style={{ fontSize: 14, color: 'var(--muted)', cursor: 'pointer', fontWeight: 500 }}
              >+ Add {mt}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
